import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { onlyOfficeService } from "../../Services/onlyOffice";
import "./styles.css";

const SCRIPT_ID = "onlyoffice-document-api";

const carregarApiOnlyOffice = (documentServerUrl) =>
  new Promise((resolve, reject) => {
    if (window.DocsAPI?.DocEditor) {
      resolve();
      return;
    }

    const src = `${String(documentServerUrl).replace(/\/$/, "")}/web-apps/apps/api/documents/api.js`;
    const existente = document.getElementById(SCRIPT_ID);
    if (existente) {
      existente.addEventListener("load", resolve, { once: true });
      existente.addEventListener(
        "error",
        () => reject(new Error("Nao foi possivel carregar o editor ONLYOFFICE.")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = () =>
      reject(new Error("Nao foi possivel carregar o editor ONLYOFFICE."));
    document.head.appendChild(script);
  });

const extrairMensagemErro = (error) =>
  error?.response?.data?.errors?.default ||
  error?.message ||
  "Nao foi possivel abrir o editor de documentos.";

const EditorOffice = () => {
  const { IDArquivo } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");
  const [nomeArquivo, setNomeArquivo] = useState("Documento");

  useEffect(() => {
    let cancelado = false;

    const abrirEditor = async () => {
      try {
        const sessao = await onlyOfficeService.criarSessao(IDArquivo);
        if (cancelado) return;

        setNomeArquivo(sessao.arquivo?.nome || "Documento");
        document.title = `${sessao.arquivo?.nome || "Documento"} - PentaGov`;
        setAviso(sessao.mensagem || "");

        if (sessao.motivo === "limite" && sessao.mensagem) {
          await Swal.fire({
            title: "Limite de edicoes atingido",
            text: sessao.mensagem,
            icon: "warning",
            confirmButtonText: "Continuar em visualizacao",
          });
          if (cancelado) return;
        }

        await carregarApiOnlyOffice(sessao.documentServerUrl);
        if (cancelado) return;
        if (!window.DocsAPI?.DocEditor) {
          throw new Error("A API do editor ONLYOFFICE nao ficou disponivel.");
        }

        editorRef.current = new window.DocsAPI.DocEditor("onlyoffice-editor", {
          ...sessao.config,
          width: "100%",
          height: "100%",
          events: {
            onAppReady: () => setCarregando(false),
            onError: (event) => {
              const codigo = event?.data?.errorCode;
              setErro(
                `O editor encontrou um erro${codigo ? ` (codigo ${codigo})` : ""}.`,
              );
              setCarregando(false);
            },
          },
        });
      } catch (error) {
        if (!cancelado) {
          setErro(extrairMensagemErro(error));
          setCarregando(false);
        }
      }
    };

    abrirEditor();
    return () => {
      cancelado = true;
      if (editorRef.current?.destroyEditor) {
        editorRef.current.destroyEditor();
      }
      editorRef.current = null;
    };
  }, [IDArquivo]);

  const fechar = () => {
    window.close();
    if (!window.closed) navigate(-1);
  };

  return (
    <main className="editor-office-page">
      <header className="editor-office-header">
        <div className="editor-office-title" title={nomeArquivo}>
          {nomeArquivo}
        </div>
        <button type="button" className="editor-office-close" onClick={fechar}>
          Fechar
        </button>
      </header>

      {aviso && <div className="editor-office-warning">{aviso}</div>}

      <section className="editor-office-content">
        {carregando && (
          <div className="editor-office-state">Carregando o editor...</div>
        )}
        {erro ? (
          <div className="editor-office-error" role="alert">
            <h1>Nao foi possivel abrir o documento</h1>
            <p>{erro}</p>
            <button type="button" onClick={fechar}>
              Fechar
            </button>
          </div>
        ) : (
          <div id="onlyoffice-editor" className="editor-office-frame" />
        )}
      </section>
    </main>
  );
};

export default EditorOffice;
